import posthog from "posthog-js";
import { NODE_ENV, POSTHOG_KEY } from "@shared/env";

// Fixed PostHog US cloud region — only changes if we ever front it with a
// same-domain reverse proxy, which would be a deliberate edit right here.
const POSTHOG_HOST = "https://us.i.posthog.com";

let initialized = false;
let identifiedUserId = null;

// Staging and production run on the SAME VITE_NODE_ENV ("production"), so
// NODE_ENV can't tell them apart. The staging app is only reachable at
// t2educacao-staging.herokuapp.com, so the host does — deriving the tag from
// it keeps staging test traffic filterable (`environment = staging`) inside
// the shared PostHog project.
function currentEnvironment() {
  return window.location.hostname.includes("staging") ? "staging" : "production";
}

// Boots the product-analytics SDK. Called once at app start (before React
// mounts) so page views and autocapture start on the very first render.
//
// The guard mirrors the Bugsnag init in App.js: skip local development (keeps
// dev noise out of the shared project) and skip when there's no key. Staging
// and production both run it — the `environment` super property registered
// below keeps their traffic separable inside the single PostHog project.
export function initPostHog() {
  if (initialized || !POSTHOG_KEY || NODE_ENV === "development") return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // PostHog's recommended "defaults snapshot" (the value from their docs).
    // It bundles the modern behavior we want: SPA-aware $pageview capture
    // (capture_pageview: 'history_change', needed for React Router navigation)
    // and $pageleave capture (needed for bounce rate and session duration).
    defaults: "2026-05-30",
    // Deliberate override of the snapshot: only identified users get a stored
    // person profile — anonymous visitors are still tracked for funnels.
    person_profiles: "identified_only",
  });

  posthog.register({ environment: currentEnvironment() });
  initialized = true;
}

// Ties the anonymous session to the logged-in student so their events roll up
// into one person. Called from the authenticated shell whenever the current
// user resolves; the id-cache keeps it a no-op on re-renders.
export function identifyPostHog(user) {
  if (!initialized || !user?.id) return;

  const distinctId = String(user.id);
  if (identifiedUserId === distinctId) return;

  identifiedUserId = distinctId;
  posthog.identify(distinctId, { email: user.email, name: user.name });
}

// Drops the identified session on sign out so the next user on this browser
// starts anonymous instead of inheriting the previous student's identity.
export function resetPostHog() {
  identifiedUserId = null;
  if (!initialized) return;

  posthog.reset();
}
